import {
    SubscriptionStatus,
    type Organization,
    type Subscription,
    type SubscriptionLine,
    type SubscriptionProductPrice,
} from '@generated/graphql';
import {
    deriveCalculationDate,
    formatCurrency,
    normalizeConnectorIds,
    selectBaselineLine,
    isPriceApplicableForDate,
} from './subscription.utils';

export interface ConnectorSummary {
    active: number;
    total: number;
    formattedPerConnector: string;
}

export interface ConnectorFeeBreakdown {
    lan?: number;
    sim?: number;
}

export type SubscriptionOrgFragment =
    | Pick<Organization, 'id' | 'subscription' | 'chargeSystems'>
    | null
    | undefined;

type AddonTotals = { ex: number; inc: number };

/** Normalize any LAN/SIM breakdown into a guaranteed object. */
export const normalizeConnectorFeeInput = (
    input: number | ConnectorFeeBreakdown
): Required<ConnectorFeeBreakdown> => {
    if (typeof input === 'number') {
        return { lan: input, sim: 0 };
    }
    return {
        lan: input.lan ?? 0,
        sim: input.sim ?? 0,
    };
};

/** Collect connector ids/counts across all charge systems on the organization. */
export const calculateConnectorMetadata = (organization?: SubscriptionOrgFragment) => {
    const connectorEntries =
        organization?.chargeSystems
            ?.flatMap((cs) => cs.connectors || [])
            .filter((connector): connector is { id: string; sim?: boolean } =>
                Boolean(connector?.id)
            )
            .map((connector) => ({
                id: connector!.id,
                sim: Boolean((connector as { sim?: boolean }).sim),
            })) ?? [];

    const lanConnectorIds: string[] = [];
    const hardwareSimConnectorIds = new Set<string>();
    connectorEntries.forEach((entry) => {
        if (entry.sim) {
            hardwareSimConnectorIds.add(entry.id);
        } else {
            lanConnectorIds.push(entry.id);
        }
    });

    const connectorIds = connectorEntries.map((entry) => entry.id);
    return {
        connectorIds,
        connectorCount: connectorIds.length,
        lanConnectorIds,
        hardwareSimConnectorIds,
    };
};

/** Resolve the calculation date and next invoice date used for pricing. */
export const calculateSubscriptionTimeline = (subscription?: Subscription | null) => {
    const nextInvoiceDate = subscription?.nextInvoiceDate
        ? new Date(subscription.nextInvoiceDate)
        : undefined;
    const calculationDate = deriveCalculationDate(subscription);
    return { nextInvoiceDate, calculationDate };
};

/** Split subscription lines into base and add-on groups for later calculations. */
export const partitionSubscriptionLines = (
    subscriptionLinesInput: (SubscriptionLine | null | undefined)[] | null | undefined,
    calculationDate: Date
) => {
    const subscriptionLines = subscriptionLinesInput ?? [];
    const baseLine = selectBaselineLine(subscriptionLines, calculationDate);
    const addonLines = subscriptionLines.filter(
        (line) => line?.product?.baseSubscriptionProduct === false
    );

    const baseLineConnectorIds = new Set(normalizeConnectorIds(baseLine?.activeConnectors));

    return { subscriptionLines, baseLine, addonLines, baseLineConnectorIds };
};

/** Derive base subscription pricing metrics from the selected baseline line. */
export const calculateBasePricing = ({
    baseLine,
    connectorCount,
    subscriptionLines,
}: {
    baseLine?: SubscriptionLine | null;
    connectorCount: number;
    subscriptionLines: (SubscriptionLine | null | undefined)[];
}) => {
    const activeConnectors = normalizeConnectorIds(baseLine?.activeConnectors);
    const paidConnectors = baseLine?.paidConnectors ?? 0;
    const numberOfInactiveConnectors = Math.max(connectorCount - activeConnectors.length, 0);
    const productPriceId = baseLine?.product?.productPrice?.productPriceId?.toString() ?? '';
    const productPrice = baseLine?.product?.productPrice;
    const price = productPrice?.price ?? 0;
    const priceInclVAT = productPrice?.priceInclVAT ?? 0;
    const billingPeriod =
        productPrice?.billingPeriod && productPrice.billingPeriod > 0
            ? productPrice.billingPeriod
            : 1;

    const perConnectorEx = price / billingPeriod;
    const perConnectorInc = priceInclVAT / billingPeriod;

    const currencyCode =
        productPrice?.currency ??
        subscriptionLines
            .map((line) => line?.product?.productPrice?.currency)
            .find((currency): currency is string => Boolean(currency));

    const baseMonthlyEx = perConnectorEx * activeConnectors.length;
    const baseMonthlyInc = perConnectorInc * activeConnectors.length;

    return {
        activeConnectors,
        paidConnectors,
        numberOfInactiveConnectors,
        productPriceId,
        perConnectorEx,
        perConnectorInc,
        currencyCode,
        baseMonthlyEx,
        baseMonthlyInc,
    };
};

/** Aggregate add-on (SIM) pricing and track which connectors are affected. */
export const calculateAddonPricing = ({
    addonLines,
    calculationDate,
    currencyCode,
    showVAT,
}: {
    addonLines: (SubscriptionLine | null | undefined)[];
    calculationDate: Date;
    currencyCode?: string;
    showVAT: boolean;
}) => {
    type BillableAddonEntry = {
        priceInfo: SubscriptionProductPrice;
        connectors: number;
        paidConnectors: number;
        activeConnectors: number;
        currency?: string;
    };
    const simConnectorIds = new Set<string>();
    const billableAddonEntries = addonLines
        .map((line): BillableAddonEntry | null => {
            const priceInfo = line?.product?.productPrice;
            if (!priceInfo || !isPriceApplicableForDate(priceInfo, calculationDate)) {
                return null;
            }
            const normalizedActive = normalizeConnectorIds(line?.activeConnectors);
            normalizedActive.forEach((id) => simConnectorIds.add(id));
            const activeCount = normalizedActive.length;
            const paidCount = line?.paidConnectors ?? 0;
            const connectors = activeCount || paidCount;
            if (!connectors) {
                return null;
            }
            return {
                priceInfo,
                connectors,
                paidConnectors: paidCount,
                activeConnectors: activeCount,
                currency: priceInfo.currency,
            };
        })
        .filter((entry): entry is BillableAddonEntry => Boolean(entry));

    const explicitSimCurrency = billableAddonEntries.find((entry) =>
        Boolean(entry.currency)
    )?.currency;

    const addonTotals: AddonTotals = billableAddonEntries.reduce(
        (acc, { priceInfo, connectors }) => {
            const period =
                priceInfo.billingPeriod && priceInfo.billingPeriod > 0
                    ? priceInfo.billingPeriod
                    : 1;
            acc.ex += ((priceInfo.price ?? 0) / period) * connectors;
            acc.inc += ((priceInfo.priceInclVAT ?? 0) / period) * connectors;
            return acc;
        },
        { ex: 0, inc: 0 }
    );

    const simActive = billableAddonEntries.reduce((acc, entry) => acc + entry.activeConnectors, 0);
    const simPaidConnectors = billableAddonEntries.reduce(
        (acc, entry) => acc + entry.paidConnectors,
        0
    );
    const simConnectorDivisor = simActive || simPaidConnectors;
    const simCurrency = explicitSimCurrency ?? currencyCode;

    const fallbackAddonPriceInfo = (() => {
        const applicable = addonLines.find((line) =>
            isPriceApplicableForDate(line?.product?.productPrice, calculationDate)
        );
        if (applicable?.product?.productPrice) {
            return applicable.product.productPrice;
        }
        return addonLines.find((line) => line?.product?.productPrice)?.product?.productPrice;
    })();

    const derivePerConnector = (
        priceInfo?: SubscriptionProductPrice | null,
        useVat = false
    ): number => {
        if (!priceInfo) return 0;
        const period =
            priceInfo.billingPeriod && priceInfo.billingPeriod > 0 ? priceInfo.billingPeriod : 1;
        const value = useVat ? priceInfo.priceInclVAT : priceInfo.price;
        return value / period;
    };

    const fallbackSimPerConnectorEx = derivePerConnector(fallbackAddonPriceInfo, false);
    const fallbackSimPerConnectorInc = derivePerConnector(fallbackAddonPriceInfo, true);

    const simPerConnectorEx = simConnectorDivisor
        ? addonTotals.ex / simConnectorDivisor
        : fallbackSimPerConnectorEx;
    const simPerConnectorInc = simConnectorDivisor
        ? addonTotals.inc / simConnectorDivisor
        : fallbackSimPerConnectorInc;

    const simSummary: ConnectorSummary | null =
        addonLines.length === 0 || !simConnectorDivisor
            ? null
            : {
                  active: simActive,
                  total: simPaidConnectors || simActive,
                  formattedPerConnector: formatCurrency(
                      showVAT ? simPerConnectorInc : simPerConnectorEx,
                      simCurrency
                  ),
              };

    return { addonTotals, simSummary, simPerConnectorEx, simPerConnectorInc, simConnectorIds };
};

/** Format the next invoice month/year label shown in the UI. */
export const getNextInvoiceLabel = (
    subscription?: Subscription | null,
    calculationDate?: Date,
    nextInvoiceDate?: Date
) => {
    const labelDate =
        nextInvoiceDate ||
        (subscription?.status === SubscriptionStatus.Draft ? calculationDate : undefined);
    return labelDate
        ? labelDate.toLocaleDateString(navigator.language, {
              month: 'long',
              year: 'numeric',
          })
        : '';
};
